import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { SortingState } from '@tanstack/react-table';
import { Alert, Box } from '@mui/material';
import { FilterableDataTable, useUrlPagination } from '@/components/common/FilterableDataTable';
import { useDateFormatter, useFetchCalls, useQualityScoreFormula } from '@/hooks';
import { TABLE_PAGINATION } from '@/constants';
import { CampaignSelector, useCampaignSelection } from '../CampaignSelector';
import { PhoneNumberSearch, usePhoneNumberSearch } from '../PhoneNumberSearch';
import getCallListColumns from './callListColumns';
import createCallListFilters from './callListFilters';

import type { Call } from '@/types/calls';
import { getUniqueCallEndReasons } from '@/utils';
import { ColumnFilterWithDisplayLabel } from '@/components/common/FilterableDataTable/TableHeader';

/**
 * Call list component that displays fetched calls data and displays it in a sortable and filterable table.
 */
const CallList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const formatDate = useDateFormatter();

    const { pagination, setPagination } = useUrlPagination();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFilterWithDisplayLabel[]>([
        { id: 'date_range', value: 'last_30_days', displayLabel: 'Last 30 Days' },
    ]);
    const { selectedCampaignId, setSelectedCampaignId } = useCampaignSelection();
    const { phoneNumber, setPhoneNumber, clearPhoneNumber } = usePhoneNumberSearch();
    const [callEndReasons, setCallEndReasons] = useState<string[] | null>([]);
    const prevCampaignIdRef = useRef<string | null>(null);
    // Fetch quality score formula ID (cached)
    const { data: qualityFormulaData } = useQualityScoreFormula(selectedCampaignId ? parseInt(selectedCampaignId) : null);
    const qualityFormulaId = qualityFormulaData?.formulaId || null;

    const reasonFilter = columnFilters.find((item) => item.id === 'reason_name')?.value;

    // Fetch calls data based on the selected campaign and phone number.
    // Calls data includes calls and agents data for the filter dropdown.
    const {
        data: callsData,
        isLoading: isLoadingCalls,
        isError: isCallsError,
        error: callsError,
    } = useFetchCalls({ sorting, columnFilters, pagination, phoneNumber, campaignId: selectedCampaignId, qualityFormulaId });

    // Fetch agents data for the filter dropdown
    const agentsData = useMemo(() => callsData?.agents || [], [callsData?.agents]);

    // Create call list filters
    const callListFilters = useMemo(() => createCallListFilters(agentsData, callEndReasons), [agentsData, callEndReasons]);

    // Create call list columns (with or without quality score formula)
    const hasQualityScoreFormula = !!qualityFormulaId;
    const callListColumns = useMemo(() => getCallListColumns({ hasQualityScoreFormula, formatDate }), [hasQualityScoreFormula, formatDate]);

    // Handle row click navigation to the call details page
    const handleRowClick = useCallback(
        (call: Call) => {
            navigate(`/calls/${call.id}`, { state: { returnUrl: `${location.pathname}${location.search}` } });
        },
        [navigate, location.pathname, location.search]
    );

    // Clear phoneNumber filter when campaign is removed
    useEffect(() => {
        if (!selectedCampaignId && phoneNumber) {
            clearPhoneNumber();
        }
    }, [selectedCampaignId, clearPhoneNumber]);

    // Update call end reasons when the campaign changes
    // or when the reason filter is reset to "All".
    useEffect(() => {
        const campaignChanged = selectedCampaignId !== prevCampaignIdRef.current;
        const isReasonFilterCleared = reasonFilter == null;

        if (!selectedCampaignId) {
            setCallEndReasons([]);
            prevCampaignIdRef.current = null;
            return;
        }

        if ((campaignChanged || isReasonFilterCleared) && callsData?.calls) {
            setCallEndReasons(getUniqueCallEndReasons(callsData.calls));
            prevCampaignIdRef.current = selectedCampaignId;
        }
    }, [selectedCampaignId, callsData?.calls, reasonFilter]);

    if (isCallsError) {
        return (
            <Alert severity="error" elevation={1}>
                Error loading calls data ({callsError?.message})
            </Alert>
        );
    }

    const tableData = callsData?.calls || [];
    const tableDataObject = callsData
        ? {
              data: callsData.calls,
              total: callsData.total,
              totalFiltered: callsData.totalFiltered,
              limit: callsData.limit,
              offset: callsData.offset,
          }
        : {
              data: tableData,
              total: 0,
              totalFiltered: 0,
              limit: pagination.pageSize,
              offset: 0,
          };

    const isLoading = isLoadingCalls;
    const noDataMessage = !selectedCampaignId
        ? 'Search for a project or select one from the dropdown to show calls'
        : 'No matching records found';

    return (
        <>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 1 }}>
                <CampaignSelector selectedCampaignId={selectedCampaignId} onCampaignChange={setSelectedCampaignId} />
                {selectedCampaignId && (
                    <PhoneNumberSearch
                        phoneNumber={phoneNumber || ''}
                        setPhoneNumber={setPhoneNumber}
                        clearPhoneNumber={clearPhoneNumber}
                        isLoading={isLoadingCalls}
                        hasData={callsData?.total !== 0}
                    />
                )}
            </Box>

            <FilterableDataTable
                data={tableDataObject}
                isLoading={isLoading}
                columns={callListColumns}
                filters={callListFilters}
                initialFilters={columnFilters}
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                onFiltersChange={setColumnFilters}
                onRowClick={handleRowClick}
                noDataMessage={noDataMessage}
                defaultRowsPerPage={TABLE_PAGINATION.DEFAULT_ROWS_PER_PAGE}
                rowsPerPageOptions={TABLE_PAGINATION.ROWS_PER_PAGE_OPTIONS}
                persistFilters={true}
                storageKey="call-list"
            />
        </>
    );
};

export default CallList;
